/* Bitute intake quiz — vanilla state machine on top of pubsub.js
   Spec: docs/intake-quiz/2026-05-04-quiz-spec.md */

(function () {
  const STORAGE_KEY = 'bitute.intake_quiz.v1';

  // ---------- Question registry (mirrors spec §"Quiz flow") ----------
  const QUESTIONS = [
    { id: 'P1', type: 'one-of', tagPrefix: 'condicion', t: 'p1' },
    { id: 'P1a', type: 'confirm', t: 'p1a', showIf: (a) => a.P1 === 'renal' },
    { id: 'P1b', type: 'confirm', t: 'p1b', showIf: (a) => a.P1 === 'hepatica' },
    { id: 'P2', type: 'one-of', tagPrefix: 'etapa', t: 'p2',
      skipIf: (a) => ['renal', 'hepatica', 'digestivo-agudo'].includes(a.P1) },
    { id: 'P3', type: 'one-of', tagPrefix: 'actividad', t: 'p3',
      skipIf: (a) => ['renal', 'hepatica', 'digestivo-agudo'].includes(a.P1) },
    { id: 'P4', type: 'one-of', tagPrefix: 'sensibilidad', t: 'p4',
      skipIf: (a) => ['renal', 'hepatica', 'alergias-piel', 'digestivo-agudo'].includes(a.P1) },
    { id: 'P5', type: 'multi', tagPrefix: 'proteina-prefiere', t: 'p5' },
    { id: 'P6', type: 'one-of-list', tagPrefix: 'raza', t: 'p6' },
    { id: 'P7', type: 'tamano', tagPrefix: 'tamano', t: 'p7' },
    { id: 'P8', type: 'contact', t: 'p8' },
  ];

  // ---------- Icon registry (per-question) ----------
  // SVG icons + emoji fallback (display: inline). Keyed by question id + value.
  const ICONS = {
    P1: {
      'ninguna':         '🐕',
      'sobrepeso':       '⚖️',
      'alergias-piel':   '🌿',
      'digestivo-agudo': '🍵',
      'renal':           '💧',
      'hepatica':        '🧬',
    },
    P2: {
      'cachorro': '🐶',
      'adulto':   '🦴',
      'senior':   '🌅',
    },
    P3: {
      'muy-activo': '🏃',
      'normal':     '🐾',
      'tranquilo':  '🛋️',
    },
    P4: {
      'estomago': '🍵',
      'piel':     '🌿',
      'ninguna':  '✨',
    },
    P5: {
      'pollo':       '🍗',
      'res':         '🥩',
      'pavo':        '🦃',
      'cordero':     '🐑',
      'cerdo':       '🐖',
    },
    // P7 (tamaño) is now a slider — no per-bucket icons.
  };

  // Question types that use compact tile layout (grid of small cards)
  // P7 was moved to a slider in v2 — no longer a tile.
  const TILE_QUESTIONS = new Set(['P2', 'P3', 'P4']);

  // ---------- Slider config (P7 — tamaño/peso) ----------
  // Single horizontal slider 1–80 kg, 0.5-kg steps. Bands derive directly
  // from the spec's 4 buckets so the slider position always maps to a
  // canonical tag (`<10` / `10-25` / `25-40` / `>40`). Tick labels sit
  // under the band centers for orientation.
  const PESO_SLIDER = {
    min: 1,
    max: 80,
    step: 0.5,
    // Band edges in kg, matching the 4 P7 buckets exactly. Used for the
    // CSS gradient AND for weightToBucket() — single source of truth.
    bands: [
      { bucket: '<10',   from: 1,  to: 10, tickAt: 5,  tickKey: 'tick_xs' },
      { bucket: '10-25', from: 10, to: 25, tickAt: 17, tickKey: 'tick_s'  },
      { bucket: '25-40', from: 25, to: 40, tickAt: 32, tickKey: 'tick_m'  },
      { bucket: '>40',   from: 40, to: 80, tickAt: 55, tickKey: 'tick_l'  },
    ],
    bucketKey: { '<10': 'bucket_xs', '10-25': 'bucket_s', '25-40': 'bucket_m', '>40': 'bucket_l' },
  };

  // Breed → typical adult weight (kg). Median between male/female for the
  // size most common in Colombia (e.g. miniature schnauzer/poodle/pinscher
  // dominate the local market, not standard). Used to pre-position the
  // P7 slider so the user starts close to the right answer. Sourced from
  // AKC/FCI breed standards. Override per breed if Bitute's catalog tells
  // us our customer base skews larger/smaller.
  const BREED_WEIGHTS = {
    'mestizo':        15,  // wide range; mid-S is the safest default for Bogotá criollos
    'labrador':       30,
    'golden':         30,
    'french-bulldog': 11,
    'schnauzer':       7,  // miniature dominates locally
    'pastor-aleman':  32,
    'yorkshire':       3,
    'poodle':          8,  // miniature dominates
    'chihuahua':       3,
    'pug':             8,
    'bulldog-ingles': 23,
    'husky':          24,
    'border-collie':  18,
    'beagle':         11,
    'pinscher':        5,  // miniature dominates
    'otra':           15,  // unknown → mid-S, user adjusts
  };
  const DEFAULT_PESO = 15;

  function weightToBucket(kg) {
    const n = parseFloat(kg);
    if (!isFinite(n) || n <= 0) return '10-25';
    for (let i = 0; i < PESO_SLIDER.bands.length; i++) {
      const b = PESO_SLIDER.bands[i];
      // Inclusive on lower edge for non-first bands so 10 → '10-25' (not '<10')
      const low  = (i === 0) ? b.from : b.from;
      const high = b.to;
      if (i === 0 ? (n < high) : (n >= low && (i === PESO_SLIDER.bands.length - 1 ? true : n < high))) {
        return b.bucket;
      }
    }
    return PESO_SLIDER.bands[PESO_SLIDER.bands.length - 1].bucket;
  }

  function breedDefaultWeight(raza) {
    if (raza && BREED_WEIGHTS[raza] !== undefined) return BREED_WEIGHTS[raza];
    return DEFAULT_PESO;
  }

  function lookupBreedLabel(state, slug) {
    try {
      const opts = (state.t.p6 && state.t.p6.options) || [];
      const hit = opts.find((o) => o.value === slug);
      return hit && hit.label;
    } catch (_) { return null; }
  }

  // Inline check icon for option pills
  const CHECK_SVG =
    '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7"/></svg>';

  // ---------- Bootstrap ----------
  function init(root) {
    const dataEl = root.querySelector('[data-quiz-data]');
    const products = JSON.parse(dataEl.getAttribute('data-products') || '[]');
    const t = JSON.parse(dataEl.getAttribute('data-strings') || '{}');
    const settings = JSON.parse(dataEl.getAttribute('data-settings') || '{}');

    const state = {
      idx: 0,
      flow: [],            // ordered list of question ids to show
      answers: restoreAnswers(),
      result: null,
      products,
      t,
      settings,
      transitioning: false,
    };

    rebuildFlow(state);
    bindIntro(root, state);
    bindEvents(root, state);

    // If user already started in a previous tab session, skip intro and resume
    if (Object.keys(state.answers).length > 0) {
      showFlow(root, state);
    } else {
      render(root, state);
    }

    console.info('[intake-quiz] init OK — products:', products.length, 'first handle:', products[0] && products[0].handle);

    // pubsub: external listeners can react to step changes / completion
    if (window.publish) {
      window.publish('intake_quiz:init', { state });
    }
  }

  // ---------- Flow / branching ----------
  function rebuildFlow(state) {
    const a = state.answers;
    const flow = [];
    QUESTIONS.forEach((q) => {
      if (q.skipIf && q.skipIf(a)) return;
      if (q.showIf && !q.showIf(a)) return;
      flow.push(q.id);
    });
    state.flow = flow;
    if (state.idx >= flow.length) state.idx = flow.length - 1;
  }

  function currentQuestion(state) {
    return QUESTIONS.find((q) => q.id === state.flow[state.idx]);
  }

  // ---------- Persistence ----------
  function restoreAnswers() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (_) { return {}; }
  }

  function persist(state) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.answers)); } catch (_) {}
  }

  // ---------- Intro / start ----------
  function bindIntro(root, state) {
    const startBtn = root.querySelector('[data-quiz-start]');
    if (!startBtn) return;
    startBtn.addEventListener('click', () => showFlow(root, state));
  }

  function showFlow(root, state) {
    const intro = root.querySelector('[data-quiz-intro]');
    const flow  = root.querySelector('[data-quiz-flow]');
    if (intro) intro.hidden = true;
    if (flow)  flow.hidden = false;
    render(root, state);

    // Phase-3 telemetry: fire "Quiz Started" once per session-start so we can
    // compute completion rate (Started → Completed) and abandoned-quiz flows
    // in Klaviyo. Guarded so a reload mid-quiz (showFlow resumes via
    // sessionStorage) doesn't double-count.
    try {
      if (!state._startedFired && window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', 'Quiz Started', {
          page_url: window.location.href,
          started_at: new Date().toISOString(),
        }]);
        state._startedFired = true;
      }
    } catch (_) {}

    // focus the prompt for screen-reader continuity
    const prompt = root.querySelector('.intake-quiz__prompt');
    if (prompt) {
      prompt.setAttribute('tabindex', '-1');
      try { prompt.focus({ preventScroll: false }); } catch (_) {}
    }
  }

  // ---------- Render ----------
  function render(root, state, opts) {
    opts = opts || {};
    const q = currentQuestion(state);
    const stepHost = root.querySelector('[data-quiz-step]');
    const progress = root.querySelector('[data-quiz-progress]');
    const navNext = root.querySelector('[data-quiz-next]');
    const navBack = root.querySelector('[data-quiz-back]');
    const stepMeta = root.querySelector('[data-quiz-meta]');

    // Detect step change so we animate between questions
    // (works without touching the engine's `advance` function).
    const previousIdx = state._lastIdx;
    const stepChanged = previousIdx !== undefined && previousIdx !== state.idx;
    state._lastIdx = state.idx;

    const apply = () => {
      progress.style.width = `${Math.round(((state.idx + 1) / state.flow.length) * 100)}%`;
      stepMeta.textContent = (state.t.step_meta || 'Paso {n} de {total}')
        .replace('{n}', state.idx + 1)
        .replace('{total}', state.flow.length);

      stepHost.innerHTML = renderStep(q, state);
      stepHost.classList.remove('is-leaving');
      stepHost.classList.add('is-entering');
      // strip transition class after it plays so re-renders (e.g. P6 "otra")
      // don't re-trigger animation
      setTimeout(() => stepHost.classList.remove('is-entering'), 320);

      navBack.disabled = state.idx === 0;
      navNext.textContent = state.idx === state.flow.length - 1
        ? (state.t.cta_finish || 'Ver mi recomendación')
        : (state.t.cta_next || 'Siguiente');
      navNext.hidden = q.type === 'one-of' || q.type === 'confirm' || q.type === 'one-of-list';
    };

    // Animate transition only when navigating between steps
    if (opts.animate || stepChanged) {
      stepHost.classList.add('is-leaving');
      setTimeout(apply, 170);
    } else {
      apply();
    }
  }

  function s(state, key, fallback) {
    return (state.t[key] !== undefined ? state.t[key] : fallback);
  }

  function iconFor(qid, value) {
    const reg = ICONS[qid];
    return reg && reg[value] ? reg[value] : '';
  }

  function optionListClass(q) {
    return TILE_QUESTIONS.has(q.id) ? 'intake-quiz__options intake-quiz__options--grid-3' : 'intake-quiz__options';
  }

  function renderOptionButton(q, opt, isSelected, extraAttrs) {
    const icon = iconFor(q.id, opt.value);
    const isTile = TILE_QUESTIONS.has(q.id);
    const tileCls = isTile ? ' intake-quiz__option--tile' : '';
    const iconHtml = icon
      ? `<span class="intake-quiz__option-icon" aria-hidden="true">${escape(icon)}</span>`
      : '';
    const subHtml = opt.sub
      ? `<span class="intake-quiz__option-sub">${escape(opt.sub)}</span>`
      : '';
    const checkHtml = isTile
      ? ''
      : `<span class="intake-quiz__option-check" aria-hidden="true">${CHECK_SVG}</span>`;
    return `
      <li>
        <button type="button"
                class="intake-quiz__option${tileCls} ${isSelected ? 'is-selected' : ''}"
                data-value="${escape(opt.value)}"
                aria-pressed="${isSelected ? 'true' : 'false'}"
                ${extraAttrs || ''}>
          ${iconHtml}
          <span class="intake-quiz__option-body">
            <span class="intake-quiz__option-label">${escape(opt.label)}</span>
            ${subHtml}
          </span>
          ${checkHtml}
        </button>
      </li>
    `;
  }

  function renderStep(q, state) {
    const t = state.t[q.t] || {};
    const prompt = t.prompt || q.id;
    const hint = t.hint || '';
    const head = `
      <h2 class="intake-quiz__prompt">${escape(prompt)}</h2>
      ${hint ? `<p class="intake-quiz__hint">${escape(hint)}</p>` : ''}
    `;

    if (q.type === 'confirm') {
      return head + `
        <div class="intake-quiz__disclaimer">${t.disclaimer || ''}</div>
        <ul class="intake-quiz__options intake-quiz__options--grid-2">
          <li><button class="intake-quiz__option intake-quiz__option--tile" data-confirm="yes" type="button"><span class="intake-quiz__option-body"><span class="intake-quiz__option-label">${escape(t.yes || 'Sí')}</span></span></button></li>
          <li><button class="intake-quiz__option intake-quiz__option--tile" data-confirm="no" type="button"><span class="intake-quiz__option-body"><span class="intake-quiz__option-label">${escape(t.no || 'No')}</span></span></button></li>
        </ul>
      `;
    }

    if (q.type === 'one-of' || q.type === 'one-of-list') {
      const current = state.answers[q.id];
      const options = (t.options || []);
      const buttons = options.map((opt) => renderOptionButton(q, opt, current === opt.value)).join('');
      const otraField = (q.id === 'P6' && current === 'otra') ? `
        <div class="intake-quiz__field" style="margin-top:0.75rem">
          <label for="raza-otra">${escape(t.otra_label || 'Cuéntanos cuál')}</label>
          <input id="raza-otra" type="text" data-extra="raza_otra_texto"
                 value="${escape(state.answers.raza_otra_texto || '')}">
        </div>
        <div class="intake-quiz__nav">
          <button type="button" class="intake-quiz__btn" data-quiz-next-extra>${escape(state.t.cta_next || 'Siguiente')}</button>
        </div>
      ` : '';
      return head + `<ul class="${optionListClass(q)}">${buttons}</ul>${otraField}`;
    }

    if (q.type === 'multi') {
      const current = Array.isArray(state.answers[q.id]) ? state.answers[q.id] : [];
      const options = (t.options || []);
      const buttons = options.map((opt) => renderOptionButton(q, opt, current.includes(opt.value), 'data-multi="1"')).join('');
      return head + `<ul class="${optionListClass(q)}">${buttons}</ul>`;
    }

    if (q.type === 'tamano') {
      // ----- Slider hydration (v2) -----
      // Priority: explicit peso_kg → breed default → 15 (DEFAULT_PESO).
      // If the user resumes a session from before the slider redesign,
      // their old P7 bucket value is honored by hydrating peso_kg from the
      // bucket midpoint so the thumb lands somewhere sensible.
      const a = state.answers;
      let peso = parseFloat(a.peso_kg);
      let usedBreedDefault = false;
      if (!isFinite(peso) || peso <= 0) {
        if (a.P7) {
          peso = midpoint(a.P7);  // resumed session backward-compat
        } else if (a.P6) {
          peso = breedDefaultWeight(a.P6);
          usedBreedDefault = true;
        } else {
          peso = DEFAULT_PESO;
        }
      }
      // Clamp + snap to slider step.
      const cfg = PESO_SLIDER;
      peso = Math.min(cfg.max, Math.max(cfg.min, Math.round(peso / cfg.step) * cfg.step));

      // Persist so downstream logic (advance, recommend) sees a value
      // even if the user never moves the thumb.
      a.peso_kg = peso;
      a.P7 = weightToBucket(peso);
      persist(state);

      // ----- Strings -----
      const unit       = t.unit       || 'kg';
      const bucketKey  = cfg.bucketKey[a.P7] || 'bucket_s';
      const bucketLabel= t[bucketKey] || '';
      const breedRaw   = (a.P6 && a.P6 !== 'otra')
        ? (lookupBreedLabel(state, a.P6) || a.P6)
        : null;
      const hint = (usedBreedDefault && breedRaw)
        ? (t.breed_hint || 'Típico para {raza} — ajústalo si quieres').replace('{raza}', breedRaw)
        : (t.breed_hint_generic || 'Ajusta el peso aproximado de tu perro');

      // Edge marks at the band boundaries (10, 25, 40 kg) — show exactly
      // where the size cuts happen. Rendered as small "notches" through
      // the rail in the section background color so they read on both
      // the filled (dark) and unfilled (light) sides of the rail.
      const range = cfg.max - cfg.min;
      const edges = [];
      cfg.bands.forEach((b, i) => {
        if (i > 0) edges.push(((b.from - cfg.min) / range) * 100);
      });
      const edgeMarks = edges.map((pct) =>
        `<span class="intake-quiz__peso-edge" style="left:${pct}%"></span>`
      ).join('');

      // Initial fill % — the rail's filled portion mirrors the thumb position
      // so the slider reads as a real progress control, not a floating dot.
      const initialPct = ((peso - cfg.min) / range) * 100;

      // Tick labels: letter (XS/S/M/L) on top, kg range below — gives the
      // user a quick read of "where am I" + "what does this band mean".
      const tickRange = (b, i) => {
        if (i === 0) return `<${b.to} kg`;
        if (i === cfg.bands.length - 1) return `>${b.from} kg`;
        return `${b.from}-${b.to} kg`;
      };
      const ticks = cfg.bands.map((b, i) => {
        const left  = ((b.tickAt - cfg.min) / range) * 100;
        const letter = t[b.tickKey] || b.bucket;
        return `
          <span class="intake-quiz__peso-tick" style="left:${left}%">
            <span class="intake-quiz__peso-tick-letter">${escape(letter)}</span>
            <span class="intake-quiz__peso-tick-range">${escape(tickRange(b, i))}</span>
          </span>`;
      }).join('');

      return head + `
        <div class="intake-quiz__peso">
          <div class="intake-quiz__peso-readout" aria-live="polite">
            <span class="intake-quiz__peso-readout-num" data-peso-num>${escape(peso)}</span>
            <span class="intake-quiz__peso-readout-unit">${escape(unit)}</span>
            <span class="intake-quiz__peso-readout-bucket" data-peso-bucket>${escape(bucketLabel)}</span>
          </div>
          <div class="intake-quiz__peso-slider-wrap" style="--peso-pct:${initialPct}%">
            <div class="intake-quiz__peso-track">
              <div class="intake-quiz__peso-slider-bands" aria-hidden="true"></div>
              ${edgeMarks}
              <input type="range"
                     class="intake-quiz__peso-slider"
                     min="${cfg.min}" max="${cfg.max}" step="${cfg.step}"
                     value="${peso}"
                     aria-label="${escape(t.peso_label || 'Peso en kg')}"
                     data-peso-slider>
            </div>
            <div class="intake-quiz__peso-ticks" aria-hidden="true">${ticks}</div>
          </div>
          <p class="intake-quiz__peso-hint">${escape(hint)}</p>
        </div>
      `;
    }

    if (q.type === 'contact') {
      const a = state.answers;
      return head + `
        <div class="intake-quiz__field">
          <label for="iq-nombre-perro">${escape(t.dog_label || 'Nombre del perro')}</label>
          <input id="iq-nombre-perro" type="text" data-extra="nombre_perro" required minlength="2"
                 value="${escape(a.nombre_perro || '')}">
        </div>
        <div class="intake-quiz__field">
          <label for="iq-nombre-dueno">${escape(t.owner_label || 'Tu nombre')}</label>
          <input id="iq-nombre-dueno" type="text" data-extra="nombre_dueno" required minlength="2"
                 value="${escape(a.nombre_dueno || '')}">
        </div>
        <div class="intake-quiz__field">
          <label for="iq-whatsapp">${escape(t.wa_label || 'WhatsApp')}</label>
          <div class="intake-quiz__phone">
            <input type="text" data-extra="whatsapp_cc" value="${escape(a.whatsapp_cc || '+57')}" aria-label="Código país">
            <input id="iq-whatsapp" type="tel" inputmode="tel" data-extra="whatsapp_local" required
                   value="${escape(a.whatsapp_local || '')}" placeholder="3001234567">
          </div>
        </div>
        <div class="intake-quiz__field">
          <label for="iq-email">${escape(t.email_label || 'Email (opcional)')}</label>
          <input id="iq-email" type="email" data-extra="email" value="${escape(a.email || '')}"
                 autocomplete="email">
        </div>
        <p class="intake-quiz__error" data-quiz-form-error></p>
      `;
    }

    return head;
  }

  // ---------- Events ----------
  function bindEvents(root, state) {
    const stepHost = root.querySelector('[data-quiz-step]');
    const navNext = root.querySelector('[data-quiz-next]');
    const navBack = root.querySelector('[data-quiz-back]');

    stepHost.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-value], button[data-confirm], [data-quiz-next-extra]');
      if (!btn) return;
      const q = currentQuestion(state);

      if (btn.hasAttribute('data-quiz-next-extra')) {
        return advance(root, state);
      }

      if (btn.dataset.confirm) {
        if (btn.dataset.confirm === 'yes') {
          state.answers[q.id] = 'confirmed';
        } else {
          // fallback: P1 → ninguna, drop confirm
          state.answers.P1 = 'ninguna';
          delete state.answers.P1a;
          delete state.answers.P1b;
        }
        persist(state);
        rebuildFlow(state);
        advance(root, state, true);
        return;
      }

      if (btn.dataset.multi) {
        // In-place toggle — avoid full re-render so the user doesn't see the
        // step host clear/rebuild on every click (causes a visible "flash"
        // because each click would otherwise tear down all option buttons).
        const cur = Array.isArray(state.answers[q.id]) ? state.answers[q.id].slice() : [];
        const v = btn.dataset.value;
        const i = cur.indexOf(v);
        if (i >= 0) cur.splice(i, 1); else cur.push(v);
        state.answers[q.id] = cur;
        persist(state);
        const nowSelected = i < 0;
        btn.classList.toggle('is-selected', nowSelected);
        btn.setAttribute('aria-pressed', nowSelected ? 'true' : 'false');
        return;
      }

      // single-value option
      state.answers[q.id] = btn.dataset.value;
      persist(state);
      rebuildFlow(state);
      // P6 "otra" needs the free-text field — re-render in place, don't auto-advance
      if (q.id === 'P6' && btn.dataset.value === 'otra') {
        render(root, state);
        return;
      }
      // P7 (tamano): user may also type peso, don't auto-advance
      if (q.id === 'P7') {
        render(root, state);
        return;
      }
      advance(root, state, true);
    });

    stepHost.addEventListener('input', (e) => {
      // P7 slider: live-update readout + bucket label without re-rendering
      // the whole step (re-render would tear down the slider mid-drag and
      // cancel the gesture on touch devices).
      const slider = e.target.closest('[data-peso-slider]');
      if (slider) {
        const v = parseFloat(slider.value);
        state.answers.peso_kg = v;
        state.answers.P7 = weightToBucket(v);
        persist(state);
        const numEl    = stepHost.querySelector('[data-peso-num]');
        const bucketEl = stepHost.querySelector('[data-peso-bucket]');
        const wrap     = stepHost.querySelector('.intake-quiz__peso-slider-wrap');
        if (numEl)    numEl.textContent = (v % 1 === 0) ? String(v) : v.toFixed(1);
        if (bucketEl) {
          const key = PESO_SLIDER.bucketKey[state.answers.P7] || 'bucket_s';
          const q = currentQuestion(state);
          const t = (q && state.t[q.t]) || {};
          bucketEl.textContent = t[key] || '';
        }
        // Update the filled-rail % so the dark fill tracks the thumb.
        if (wrap) {
          const pct = ((v - PESO_SLIDER.min) / (PESO_SLIDER.max - PESO_SLIDER.min)) * 100;
          wrap.style.setProperty('--peso-pct', pct + '%');
        }
        return;
      }

      const el = e.target.closest('[data-extra]');
      if (!el) return;
      state.answers[el.dataset.extra] = el.value;
      persist(state);
    });

    navNext.addEventListener('click', () => advance(root, state));
    navBack.addEventListener('click', () => {
      if (state.idx > 0) { state.idx--; render(root, state); }
    });
  }

  function advance(root, state, fromOption) {
    const q = currentQuestion(state);

    // Validate current
    if (q.type === 'multi' && !(state.answers[q.id] || []).length) {
      flashError(root, state.t.err_pick_one || 'Elige al menos una opción.');
      return;
    }
    if ((q.type === 'one-of' || q.type === 'one-of-list' || q.type === 'tamano') && !state.answers[q.id]) {
      flashError(root, state.t.err_pick_one || 'Elige una opción.');
      return;
    }
    if (q.type === 'contact') {
      const err = validateContact(state);
      if (err) {
        const errEl = root.querySelector('[data-quiz-form-error]');
        if (errEl) errEl.textContent = err;
        return;
      }
      return submitQuiz(root, state);
    }

    if (state.idx < state.flow.length - 1) {
      state.idx++;
      render(root, state);
    } else if (q.type !== 'contact') {
      // shouldn't happen — last is always P8
      render(root, state);
    }
  }

  function flashError(root, msg) {
    let host = root.querySelector('[data-quiz-flash]');
    if (!host) {
      host = document.createElement('p');
      host.className = 'intake-quiz__error';
      host.setAttribute('data-quiz-flash', '');
      root.querySelector('[data-quiz-step]').appendChild(host);
    }
    host.textContent = msg;
    setTimeout(() => { host.textContent = ''; }, 2400);
  }

  function validateContact(state) {
    const a = state.answers;
    if (!a.nombre_perro || a.nombre_perro.trim().length < 2) return 'Cuéntanos el nombre de tu perro.';
    if (!a.nombre_dueno || a.nombre_dueno.trim().length < 2) return 'Necesitamos tu nombre.';
    const digits = (a.whatsapp_local || '').replace(/\D/g, '');
    if (digits.length < 7) return 'Revisa tu número de WhatsApp.';
    if (a.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) return 'El email no parece válido.';
    return null;
  }

  // ---------- Recommendation engine ----------
  function recommend(state) {
    const a = state.answers;
    const products = state.products;
    const cond = a.P1;

    // Direct shortcuts
    const SHORTCUTS = {
      renal: ['renal', 'pavo-cebada'],
      hepatica: ['hepatico-pavo', 'tofu-y-amaranto'],
      'digestivo-agudo': ['astringente-pavo', 'pavo-linaza'],
      'alergias-piel': ['cordero', 'pavo-linaza'],
      sobrepeso: ['pollo', 'pavo-cebada'],
    };

    let principal, alterno;

    if (SHORTCUTS[cond] && (cond !== 'renal' || a.P1a === 'confirmed') && (cond !== 'hepatica' || a.P1b === 'confirmed')) {
      const [pH, aH] = SHORTCUTS[cond];
      principal = findByHandle(products, pH);
      alterno = findByHandle(products, aH);
    } else {
      // Score-based
      const proteinaEvitar = []; // not captured separately — could derive from inverse of P5 in future
      const ranked = products
        .filter((p) => !(p.tags || []).includes('category:snack'))
        .map((p) => ({ p, s: scoreProduct(p, a, proteinaEvitar) }))
        .sort((x, y) => y.s - x.s);
      principal = ranked[0] && ranked[0].p;
      alterno = ranked[1] && ranked[1].p;
    }

    // Meal plan
    // pesoKg (kg) × pct (decimal, e.g. 0.025) × 1000 (kg→g) = grams of food per day.
    // Phase 1 used ×10 which underestimated by 100x and capped paquetes_mes at 1.
    const pesoKg = parseFloat(a.peso_kg) || midpoint(a.P7);
    const pct = dailyPct(a);
    const gramosDia = Math.round(pesoKg * pct * 1000);
    const paquetesMes = Math.max(1, Math.ceil(gramosDia * 30 / 200));
    const principalPrice = principal ? principal.price : 0;
    const gastoMes = paquetesMes * principalPrice;
    const grandesAmigos = gastoMes >= (state.settings.plan_threshold_cop || 500000);

    return {
      principal_handle: principal && principal.handle,
      alterno_handle: alterno && alterno.handle,
      principal_match_reasons: principal ? matchReasons(principal, a) : [],
      alterno_match_reasons: alterno ? matchReasons(alterno, a) : [],
      peso_kg: pesoKg,
      porcion_g_dia: gramosDia,
      paquetes_mes: paquetesMes,
      gasto_mes_cop: gastoMes,
      plan_grandes_amigos_eligible: grandesAmigos,
    };
  }

  // Why was a product picked? Up to 4 short, human-readable reasons derived
  // from the overlap of the product's tags and the user's answers. The result
  // card renders these as small badges/chips so the recommendation feels
  // earned rather than algorithmic.
  function matchReasons(p, a) {
    const tags = p.tags || [];
    const out = [];
    const pretty = {
      cachorro: 'Etapa cachorro',
      adulto: 'Etapa adulta',
      senior: 'Perro mayor',
      sobrepeso: 'Control de peso',
      renal: 'Sensibilidad renal',
      hepatica: 'Sensibilidad hepática',
      'digestivo-agudo': 'Episodio digestivo',
      'alergias-piel': 'Alergias / piel',
      pollo: 'Pollo',
      res: 'Res',
      pavo: 'Pavo',
      cordero: 'Cordero',
      cerdo: 'Cerdo',
    };
    if (a.P1 && a.P1 !== 'ninguna' &&
        (tags.includes('condition:' + a.P1) || tags.includes('good-for:' + a.P1))) {
      out.push(pretty[a.P1] || a.P1);
    }
    if (a.P2 && tags.includes('life-stage:' + a.P2)) out.push(pretty[a.P2] || a.P2);
    if (a.P3 === 'muy-activo' && (tags.includes('good-for:activo') || tags.includes('good-for:alta-proteina'))) {
      out.push('Perro activo');
    }
    if (a.P4 === 'estomago' && (tags.includes('good-for:digestion-sensible') || tags.includes('good-for:digestion-suave'))) {
      out.push('Estómago sensible');
    }
    if (a.P4 === 'piel' && tags.includes('good-for:piel-pelaje')) out.push('Piel y pelaje');
    (a.P5 || []).forEach((pref) => {
      if (out.length < 4 && tags.includes('protein:' + pref)) {
        out.push('Proteína: ' + (pretty[pref] || pref));
      }
    });
    return out.slice(0, 4);
  }

  function findByHandle(list, handle) { return list.find((p) => p.handle === handle); }

  function scoreProduct(p, a, proteinaEvitar) {
    const tags = p.tags || [];
    let s = 0;
    // condicion → good-for or condition tags
    if (a.P1 && a.P1 !== 'ninguna') {
      if (tags.includes(`condition:${a.P1}`)) s += 3;
      if (tags.includes(`good-for:${a.P1}`)) s += 3;
    }
    // etapa
    if (a.P2 && tags.includes(`life-stage:${a.P2}`)) s += 2;
    if (a.P2 && tags.includes(`good-for:${a.P2}`)) s += 1;
    // actividad
    if (a.P3 === 'muy-activo' && (tags.includes('good-for:activo') || tags.includes('good-for:alta-proteina'))) s += 2;
    if (a.P3 === 'tranquilo' && tags.includes('good-for:mantenimiento')) s += 1;
    // sensibilidad
    if (a.P4 === 'estomago' && tags.includes('good-for:digestion-sensible')) s += 1;
    if (a.P4 === 'estomago' && tags.includes('good-for:digestion-suave')) s += 1;
    if (a.P4 === 'piel' && tags.includes('good-for:piel-pelaje')) s += 1;
    // proteinas preferidas
    (a.P5 || []).forEach((pref) => { if (tags.includes(`protein:${pref}`)) s += 1; });
    // penalties
    if (tags.includes('not-for:perro-sano') && (!a.P1 || a.P1 === 'ninguna')) s -= 5;
    proteinaEvitar.forEach((pref) => { if (tags.includes(`protein:${pref}`)) s -= 3; });
    return s;
  }

  function dailyPct(a) {
    if (a.P2 === 'cachorro') return 0.05;
    if (a.P2 === 'senior') return 0.02;
    if (a.P1 === 'sobrepeso') return 0.02;
    if (a.P1 && !['ninguna'].includes(a.P1)) return 0.025;
    if (a.P2 === 'adulto') {
      if (a.P3 === 'muy-activo') return 0.03;
      if (a.P3 === 'tranquilo') return 0.02;
      return 0.025;
    }
    return 0.025;
  }

  function midpoint(t) {
    return ({ '<10': 7, '10-25': 17, '25-40': 32, '>40': 48 })[t] || 17;
  }

  function kcalEstimate(state) {
    const a = state.answers || {};
    const s = state.settings || {};
    const peso = parseFloat(a.peso_kg) || midpoint(a.P7);
    if (!peso || peso <= 0) return null;
    const f = (v, d) => {
      const n = typeof v === 'number' ? v : parseFloat(v);
      return isFinite(n) && n > 0 ? n : d;
    };
    let factor;
    if (a.P2 === 'cachorro') factor = f(s.mer_cachorro, 2.5);
    else if (a.P2 === 'senior') factor = f(s.mer_senior, 1.3);
    else if (a.P1 === 'sobrepeso') factor = f(s.mer_sobrepeso, 1.0);
    else if (a.P2 === 'adulto' && a.P3 === 'muy-activo') factor = f(s.mer_adulto_muy_activo, 1.7);
    else if (a.P2 === 'adulto' && a.P3 === 'tranquilo') factor = f(s.mer_adulto_tranquilo, 1.2);
    else if (a.P2 === 'adulto') factor = f(s.mer_adulto_normal, 1.4);
    else if (a.P1 && a.P1 !== 'ninguna') factor = f(s.mer_condicion, 1.4);
    else factor = f(s.mer_adulto_normal, 1.4);
    const rer = 70 * Math.pow(peso, 0.75);
    return Math.round(rer * factor);
  }

  // ---------- Submit ----------
  function submitQuiz(root, state) {
    const result = recommend(state);
    state.result = result;

    const a = state.answers;
    const tags = ['quiz:completed', `quiz:condicion-${a.P1 || 'ninguna'}`];
    if (a.P2) tags.push(`quiz:etapa-${a.P2}`);
    if (a.P3) tags.push(`quiz:actividad-${a.P3}`);
    if (a.P4) tags.push(`quiz:sensibilidad-${a.P4}`);
    if (a.P7) tags.push(`quiz:tamano-${a.P7}`);
    (a.P5 || []).forEach((p) => tags.push(`quiz:proteina-prefiere-${p}`));
    if (a.P6) tags.push(`quiz:raza-${a.P6}`);
    if (result.plan_grandes_amigos_eligible) tags.push('quiz:plan-grandes-amigos-eligible');

    const phone = ((a.whatsapp_cc || '+57') + (a.whatsapp_local || '')).replace(/[^\d+]/g, '');

    const payload = {
      condicion: a.P1 || 'ninguna',
      etapa_vida: a.P2 || null,
      actividad: a.P3 || null,
      sensibilidad: a.P4 || null,
      proteina_pref: a.P5 || [],
      raza: a.P6 || null,
      raza_otra_texto: a.raza_otra_texto || null,
      tamano: a.P7 || null,
      peso_kg: parseFloat(a.peso_kg) || null,
      nombre_perro: a.nombre_perro,
      nombre_dueno: a.nombre_dueno,
      whatsapp: phone,
      email: a.email || null,
      recomendacion_principal_handle: result.principal_handle,
      recomendacion_alterna_handle: result.alterno_handle,
      porcion_g_dia: result.porcion_g_dia,
      paquetes_mes: result.paquetes_mes,
      gasto_mes_cop: result.gasto_mes_cop,
      plan_grandes_amigos_eligible: result.plan_grandes_amigos_eligible,
      completed_at: new Date().toISOString(),
    };

    // Persist full result so result snippet (post-redirect) can render
    try { sessionStorage.setItem(STORAGE_KEY + '.result', JSON.stringify({ payload, result, tags })); } catch (_) {}

    if (window.publish) window.publish('intake_quiz:completed', { payload, result });

    // Lead capture deferred to Phase 2.
    //
    // Pattern A (storefront {% form 'customer' %}) was attempted and abandoned
    // after these blockers, all from Shopify's storefront security model:
    //   1. Storefront forms cannot write metafields (`contact[metafields][...]`
    //      causes silent submit rejection).
    //   2. Shopify's CAPTCHA listener fetches a verification token cross-origin;
    //      on a custom-domain preview (bitute.co vs *.myshopify.com) that fetch
    //      fails and the submit hangs indefinitely.
    //   3. Even the simpler "click a hidden submitter" workaround couldn't get
    //      past CAPTCHA reliably.
    //
    // For Phase 2 the right tool is Klaviyo: the SDK identifies a profile by
    // phone or email and lets us set arbitrary properties — no metafield
    // restrictions, no CAPTCHA fight. Until then, we surface the full payload
    // on the page (sessionStorage) and emit a pubsub event so any future
    // listener (a Klaviyo snippet, a Make.com webhook, a Shopify Function) can
    // catch the lead with zero changes here.
    console.info('[intake-quiz] lead captured (Phase 2 will forward to ESP):', {
      payload,
      tags,
      result,
    });

    // ---- Phase 2: Klaviyo lead capture ----
    // The Klaviyo onsite snippet (Public Key SxsY4s) is loaded in layout/theme.liquid.
    // We forward two things: (1) a profile identify so segments/flows can target the
    // user by phone/email + quiz properties; (2) a "Quiz Completed" event with the
    // rich payload so flows can branch on recomendation/plan/condicion. Both calls
    // are guarded — if Klaviyo fails to load (ad-blocker, network), the quiz
    // continues to render the result normally.
    try {
      const k = window.klaviyo;
      if (k && typeof k.push === 'function') {
        const identify = {
          $phone_number: payload.whatsapp || undefined,  // E.164, e.g. +573001234567
          $email:        payload.email   || undefined,
          $first_name:   payload.nombre_dueno || undefined,
          dog_name:      payload.nombre_perro || undefined,
          raza:          payload.raza || payload.raza_otra_texto || undefined,
          tamano:        payload.tamano || undefined,
          peso_kg:       payload.peso_kg || undefined,
          condicion:     payload.condicion || undefined,
          etapa_vida:    payload.etapa_vida || undefined,
          actividad:     payload.actividad || undefined,
          sensibilidad:  payload.sensibilidad || undefined,
          proteinas_pref:      payload.proteina_pref || [],
          plan_grandes_amigos: payload.plan_grandes_amigos_eligible,
        };
        const trackProps = {
          recomendacion_principal: payload.recomendacion_principal_handle,
          recomendacion_alterna:   payload.recomendacion_alterna_handle,
          porcion_g_dia:           payload.porcion_g_dia,
          paquetes_mes:            payload.paquetes_mes,
          gasto_mes_cop:           payload.gasto_mes_cop,
          plan_grandes_amigos_eligible: payload.plan_grandes_amigos_eligible,
          completed_at:            payload.completed_at,
          quiz_tags:               tags,
        };
        // The 2024+ klaviyo.js exposes both a queue-style push() API and
        // direct identify()/track() methods. Push is the most resilient even
        // if the SDK hasn't fully initialized yet.
        k.push(['identify', identify]);
        k.push(['track', 'Quiz Completed', trackProps]);
        console.info('[intake-quiz] forwarded to Klaviyo:', { identify, trackProps });
      } else {
        console.warn('[intake-quiz] window.klaviyo not available — lead not forwarded (ad-blocker or snippet missing).');
      }
    } catch (err) {
      console.warn('[intake-quiz] Klaviyo forward failed (non-fatal):', err);
    }

    renderResult(root, state);
  }

  // ---------- Result render ----------
  function renderLoading(root, state, host, quizArea) {
    const lt = (state.t.loading) || {};
    const dogName = (state.answers && state.answers.nombre_perro) || '';
    const title = (lt.title || 'Personalizando el plato de {nombre}…').replace('{nombre}', dogName).trim();
    host.hidden = false;
    quizArea.hidden = true;
    host.innerHTML = `
      <div class="intake-quiz__loading" role="status" aria-live="polite">
        <div class="intake-quiz__loading-orbit" aria-hidden="true"></div>
        <h2 class="intake-quiz__loading-title">${escape(title)}</h2>
        <ul class="intake-quiz__loading-steps">
          <li>${escape(lt.step_1 || 'Cruzando lo que nos contaste…')}</li>
          <li>${escape(lt.step_2 || 'Eligiendo el menú que mejor le cae…')}</li>
          <li>${escape(lt.step_3 || 'Calculando porciones y plan mensual…')}</li>
        </ul>
      </div>
    `;
  }

  function renderResult(root, state) {
    const host = root.querySelector('[data-quiz-result]');
    const quizArea = root.querySelector('[data-quiz-flow]');
    if (!host) { console.error('[intake-quiz] no result host'); return; }

    // Loading transition — sits in the rendering path only, so it doesn't
    // collide with the engine session's submitQuiz changes.
    state.transitioning = true;
    try {
      renderLoading(root, state, host, quizArea);
    } catch (_) {}

    setTimeout(() => {
      state.transitioning = false;
      try {
        _renderResult(root, state, host, quizArea);
      } catch (err) {
        console.error('[intake-quiz] renderResult failed:', err);
        renderResultError(host, err, state);
      }
    }, 1500);
  }

  function renderResultError(host, err, state) {
    host.hidden = false;
    host.innerHTML = `
      <div style="padding:1.5rem;border:1px solid #c00;border-radius:8px;background:#fff3f3;color:#222">
        <h3 style="margin:0 0 .5rem">Algo se rompió al armar tu resultado</h3>
        <p style="margin:0 0 .75rem">Compártele este mensaje al equipo de Bitute:</p>
        <pre style="white-space:pre-wrap;font-size:.85rem;background:#fff;padding:.75rem;border-radius:4px;overflow:auto">${escape(err && err.stack || String(err))}</pre>
        <details style="margin-top:.75rem"><summary>state debug</summary>
          <pre style="white-space:pre-wrap;font-size:.75rem">${escape(JSON.stringify({ products_count: (state.products||[]).length, result: state.result, answers: state.answers }, null, 2))}</pre>
        </details>
      </div>`;
  }

  function _renderResult(root, state, host, quizArea) {
    const result = state.result;
    const a = state.answers;
    const principal = findByHandle(state.products, result.principal_handle);
    const alterno = findByHandle(state.products, result.alterno_handle);

    host.hidden = false;
    quizArea.hidden = true;

    const t = state.t.result || {};
    const fmt = (n) => '$' + Math.round(n).toLocaleString('es-CO');
    const dogName = a.nombre_perro || 'tu perro';

    const renderChips = (reasons) => {
      if (!Array.isArray(reasons) || !reasons.length) return '';
      const items = reasons
        .map((r) => `<li class="intake-quiz__chip">${escape(r)}</li>`)
        .join('');
      return `<ul class="intake-quiz__chips" aria-label="Por qué este plato">${items}</ul>`;
    };

    const principalCard = (p) => {
      if (!p) return '';
      const reason = (p.recommendation_reason || '').trim();
      const reasonLabel = (t.reason_label || 'Por qué se lo recomendamos a {nombre}').replace('{nombre}', dogName);
      const chips = renderChips(result.principal_match_reasons);
      return `
        <article class="intake-quiz__pick">
          <div class="intake-quiz__pick-media">
            ${p.image ? `<img src="${escape(p.image)}" alt="${escape(p.title)}" loading="lazy">` : ''}
          </div>
          <div class="intake-quiz__pick-body">
            <span class="intake-quiz__pick-tag">${escape(t.role_principal || 'Tu plato principal')}</span>
            <h3 class="intake-quiz__pick-name">${escape(p.title)}</h3>
            <p class="intake-quiz__pick-price">${fmt(p.price)} / porción de 200g</p>
            ${chips}
            ${reason ? `
              <span class="intake-quiz__pick-reason-label">${escape(reasonLabel)}</span>
              <p class="intake-quiz__pick-reason">${escape(reason)}</p>
            ` : ''}
            <a class="intake-quiz__btn intake-quiz__btn--lg intake-quiz__btn--block" href="/products/${escape(p.handle)}">${escape(t.cta_principal || 'Pedir ahora')}</a>
          </div>
        </article>
      `;
    };

    const alternoCard = (p) => {
      if (!p) return '';
      const chips = renderChips(result.alterno_match_reasons);
      return `
        <div class="intake-quiz__alt-wrap">
          <span class="intake-quiz__alt-label">${escape(t.alt_label || 'Otra opción que también le va')}</span>
          <article class="intake-quiz__pick--alt">
            <div class="intake-quiz__pick-media">
              ${p.image ? `<img src="${escape(p.image)}" alt="${escape(p.title)}" loading="lazy">` : ''}
            </div>
            <div class="intake-quiz__pick-body">
              <span class="intake-quiz__pick-tag">${escape(t.role_alterno || 'Alternativa')}</span>
              <h4 class="intake-quiz__pick-name">${escape(p.title)}</h4>
              <p class="intake-quiz__pick-price">${fmt(p.price)} / 200g</p>
              ${chips}
              <a class="intake-quiz__pick--alt-link" href="/products/${escape(p.handle)}">${escape(t.cta_alt || 'Ver detalles')} →</a>
            </div>
          </article>
        </div>
      `;
    };

    const kcal = kcalEstimate(state);
    const diasPaquete = Math.max(1, Math.ceil(200 / Math.max(1, result.porcion_g_dia)));
    const costoDia = Math.round(result.gasto_mes_cop / 30);

    const planChips = (() => {
      const items = [];
      if (kcal) items.push((t.chip_kcal || '~{n} kcal/día').replace('{n}', kcal.toLocaleString('es-CO')));
      items.push((t.chip_days || '1 paquete = ~{n} días').replace('{n}', diasPaquete));
      items.push((t.chip_cost || '~{x}/día').replace('{x}', fmt(costoDia)));
      return `<ul class="intake-quiz__chips" aria-label="Detalles del plan">${
        items.map((r) => `<li class="intake-quiz__chip">${escape(r)}</li>`).join('')
      }</ul>`;
    })();

    const mealPlanCard = () => {
      const isAmigos = result.plan_grandes_amigos_eligible;
      const eyebrow = isAmigos
        ? (t.plan_amigos_eyebrow || 'Plan Grandes Amigos')
        : (t.plan_eyebrow || 'Tu plan mensual');
      const title = isAmigos
        ? (t.plan_amigos_title || 'Califican para Plan Grandes Amigos')
        : (t.plan_title || 'Tu plan mensual');
      const body = isAmigos
        ? (t.plan_summary || 'Con tu plan mensual gastarías ~{x}.').replace('{x}', fmt(result.gasto_mes_cop))
        : (t.plan_summary_std || '{nombre} necesita ~{n} {paquetes}/mes — ~{x}.')
            .replace('{nombre}', a.nombre_perro || 'Tu perro')
            .replace('{n}', result.paquetes_mes)
            .replace('{paquetes}', pluralize(result.paquetes_mes, 'paquete', 'paquetes'))
            .replace('{x}', fmt(result.gasto_mes_cop));
      const ctaHref = isAmigos
        ? 'https://wa.me/573003313010?text=Hola%2C%20completé%20el%20quiz%20y%20califico%20para%20Plan%20Grandes%20Amigos'
        : '/collections/all';
      const ctaLabel = isAmigos
        ? (t.cta_wa || 'Hablar con el equipo')
        : (t.cta_shop || 'Pide tu primer pedido');
      const amigosPerks = isAmigos ? `
        <ul class="intake-quiz__plan-perks">
          <li>${escape(t.perk_discount || '15% off en menús seleccionados')}</li>
          <li>${escape(t.perk_anticipado || 'Pedidos mínimos $200,000 · pago anticipado')}</li>
        </ul>
      ` : '';
      return `
        <article class="intake-quiz__pick intake-quiz__pick--plan${isAmigos ? ' intake-quiz__pick--plan-amigos' : ''}">
          <div class="intake-quiz__pick-media intake-quiz__plan-media" aria-hidden="true">
            <span class="intake-quiz__plan-bignum">${result.paquetes_mes}</span>
            <span class="intake-quiz__plan-bigsub">${escape(pluralize(result.paquetes_mes, 'paquete', 'paquetes'))} / mes</span>
          </div>
          <div class="intake-quiz__pick-body">
            <span class="intake-quiz__pick-tag">${escape(eyebrow)}</span>
            <h3 class="intake-quiz__pick-name">${escape(title)}</h3>
            <p class="intake-quiz__pick-price">${fmt(result.gasto_mes_cop)} / mes estimado</p>
            ${planChips}
            <p class="intake-quiz__pick-reason">${escape(body)}</p>
            ${amigosPerks}
            <a class="intake-quiz__btn intake-quiz__btn--lg intake-quiz__btn--block" href="${ctaHref}">${escape(ctaLabel)}</a>
          </div>
        </article>
      `;
    };

    const planBlock = mealPlanCard();

    host.innerHTML = `
      <div class="intake-quiz__result">
        <header class="intake-quiz__result-head">
          <span class="intake-quiz__result-eyebrow">${escape(t.eyebrow || 'Tu recomendación personalizada')}</span>
          <h2 class="intake-quiz__result-title">${escape((t.title || 'Listo, {nombre} 🐾').replace('{nombre}', a.nombre_perro || ''))}</h2>
          <p class="intake-quiz__result-sub">${escape(t.sub || 'Basado en lo que nos contaste.')}</p>
        </header>
        <div class="intake-quiz__result-twin">
          ${principalCard(principal)}
          ${planBlock}
        </div>
        ${alternoCard(alterno)}
      </div>
    `;

    if (window.publish) window.publish('intake_quiz:rendered_result', { result });
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }

  // ---------- Util ----------
  function pluralize(n, sing, plur) { return n === 1 ? sing : plur; }

  function escape(v) {
    if (v === undefined || v === null) return '';
    return String(v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ---------- Boot ----------
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-intake-quiz]').forEach(init);
  });
})();
